import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, qs, type Paged } from '../api';
import { useAdmin } from '../auth';
import { Btn, Empty, ErrorBox, Loading, Modal, Pager, REPORT_REASONS, ROLE_RU, STATUS_RU, fmtDate, useAction } from '../ui';

interface UserRow {
  id: string; telegramId: string; username: string | null; firstName: string; status: string; blockedReason: string | null;
  createdAt: string; lastSeenAt: string | null; listings: number; reportsAgainst: number;
}

export function UsersPage() {
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState<string | null>(null);
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['users', q, status, page],
    queryFn: () => api<Paged<UserRow>>(`/admin/users${qs({ q, status, page, pageSize: 20 })}`),
  });
  return (
    <div>
      <h1>Пользователи</h1>
      <div className="toolbar">
        <input placeholder="Имя, @username, Telegram ID или ID" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} />
        <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
          <option value="">Все</option>
          <option value="active">Активные</option>
          <option value="blocked">Заблокированные</option>
          <option value="deleted">Удалённые</option>
        </select>
      </div>
      {isLoading ? <Loading /> : error ? <ErrorBox error={error} onRetry={() => void refetch()} /> : !data?.items.length ? <Empty /> : (
        <>
          <table>
            <thead><tr><th>Пользователь</th><th>Статус</th><th>Объявлений</th><th>Жалоб</th><th>Регистрация</th><th>Был в сети</th></tr></thead>
            <tbody>
              {data.items.map((u) => (
                <tr key={u.id} className="clickable" onClick={() => setOpen(u.id)}>
                  <td><strong>{u.firstName || '—'}</strong> {u.username ? `@${u.username}` : ''}<div className="muted">#{u.id} · TG {u.telegramId}</div></td>
                  <td>{STATUS_RU[u.status] ?? u.status}{u.blockedReason ? <div className="muted">{u.blockedReason}</div> : null}</td>
                  <td>{u.listings}</td>
                  <td>{u.reportsAgainst ? <span className="pill pill-danger">{u.reportsAgainst}</span> : 0}</td>
                  <td>{fmtDate(u.createdAt)}</td>
                  <td>{fmtDate(u.lastSeenAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pager page={page} pageSize={20} total={data.total} onPage={setPage} />
        </>
      )}
      {open ? <UserCard id={open} onClose={() => setOpen(null)} /> : null}
    </div>
  );
}

function UserCard({ id, onClose }: { id: string; onClose: () => void }) {
  const qc = useQueryClient();
  const { can } = useAdmin();
  const action = useAction();
  const [reason, setReason] = useState('');
  const { data: u, isLoading, error } = useQuery({
    queryKey: ['user', id],
    queryFn: () =>
      api<{
        id: string; telegramId: string; username: string | null; firstName: string; status: string; blockedReason: string | null; avatarUrl: string | null;
        createdAt: string; lastSeenAt: string | null; botStarted: boolean;
        listings: { id: string; title: string; status: string; updatedAt: string }[];
        reports: { id: string; reason: string; status: string; listingId: string | null; createdAt: string }[];
      }>(`/admin/users/${id}`),
  });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['user', id] });
    void qc.invalidateQueries({ queryKey: ['users'] });
  };
  return (
    <Modal title={`Пользователь #${id}`} onClose={onClose}>
      {isLoading ? <Loading /> : error || !u ? <ErrorBox error={error} /> : (
        <div>
          <div className="row user-head">
            {u.avatarUrl ? <img className="user-avatar" src={u.avatarUrl} alt="Фото профиля" width={56} height={56} /> : null}
            <p><strong>{u.firstName}</strong> {u.username ? `@${u.username}` : ''} · TG {u.telegramId}</p>
            {u.avatarUrl && can('users.block') ? (
              <Btn kind="secondary" busy={action.busy} onClick={() => void action.run(async () => { await api(`/admin/users/${id}/avatar/remove`, { method: 'POST' }); refresh(); })}>
                Удалить фото
              </Btn>
            ) : null}
          </div>
          <p>Статус: {STATUS_RU[u.status] ?? u.status} {u.blockedReason ? `(${u.blockedReason})` : ''} · бот {u.botStarted ? 'запущен' : 'не запущен'}</p>
          <p className="muted">Регистрация {fmtDate(u.createdAt)} · последний вход {fmtDate(u.lastSeenAt)}</p>
          <h3>Объявления</h3>
          {u.listings.length ? <ul>{u.listings.map((l) => <li key={l.id}>#{l.id} {l.title} — {STATUS_RU[l.status]}</li>)}</ul> : <Empty text="Нет" />}
          <h3>Жалобы на пользователя и его объявления</h3>
          {u.reports.length ? <ul>{u.reports.map((r) => <li key={r.id}>{REPORT_REASONS[r.reason]} — {STATUS_RU[r.status]} ({fmtDate(r.createdAt)})</li>)}</ul> : <Empty text="Нет" />}
          {can('users.block') && u.status === 'active' ? (
            <div className="decision">
              <input placeholder="Основание блокировки (обязательно)" value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} />
              <Btn kind="danger" disabled={reason.trim().length < 3} busy={action.busy} onClick={() => void action.run(async () => { await api(`/admin/users/${id}/block`, { method: 'POST', json: { reason: reason.trim() } }); refresh(); })}>Заблокировать</Btn>
            </div>
          ) : null}
          {can('users.block') && u.status === 'blocked' ? (
            <Btn busy={action.busy} onClick={() => void action.run(async () => { await api(`/admin/users/${id}/unblock`, { method: 'POST' }); refresh(); })}>Разблокировать</Btn>
          ) : null}
          {action.error ? <ErrorBox error={action.error} /> : null}
        </div>
      )}
    </Modal>
  );
}

export function DeletionRequestsPage() {
  const qc = useQueryClient();
  const action = useAction();
  const [status, setStatus] = useState('pending');
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['deletion', status],
    queryFn: () => api<Paged<{ id: string; status: string; requestedAt: string; processedAt: string | null; dueAt: string; overdue: boolean; user: { id: string; firstName: string; username: string | null; status: string } }>>(`/admin/deletion-requests${qs({ status })}`),
  });
  const act = (id: string, kind: 'execute' | 'reject') =>
    action.run(async () => {
      if (kind === 'execute' && !window.confirm('Удалить объявления и обезличить данные пользователя? Действие необратимо.')) return;
      const reason = kind === 'reject' ? window.prompt('Причина отказа — её получит пользователь в боте и она будет записана в журнал') : null;
      if (kind === 'reject' && (!reason || reason.trim().length < 3)) return;
      await api(`/admin/deletion-requests/${id}/${kind}`, { method: 'POST', json: kind === 'reject' ? { reason } : {} });
      void qc.invalidateQueries({ queryKey: ['deletion'] });
    });
  return (
    <div>
      <h1>Запросы на удаление аккаунта</h1>
      <div className="toolbar">
        <select value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="pending">Ожидают</option>
          <option value="completed">Выполнены</option>
          <option value="rejected">Отклонены</option>
        </select>
      </div>
      {action.error ? <ErrorBox error={action.error} /> : null}
      {isLoading ? <Loading /> : error ? <ErrorBox error={error} onRetry={() => void refetch()} /> : !data?.items.length ? <Empty text="Запросов нет" /> : (
        <table>
          <thead><tr><th>Пользователь</th><th>Запрошено</th><th>Ответить до</th><th>Обработано</th><th /></tr></thead>
          <tbody>
            {data.items.map((r) => (
              <tr key={r.id} className={r.overdue ? 'overdue' : undefined}>
                <td>{r.user.firstName || '—'} {r.user.username ? `@${r.user.username}` : ''} #{r.user.id}</td>
                <td>{fmtDate(r.requestedAt)}</td>
                <td>{r.status === 'pending' ? <>{fmtDate(r.dueAt)}{r.overdue ? <span className="pill pill-danger">просрочено</span> : null}</> : '—'}</td>
                <td>{fmtDate(r.processedAt)}</td>
                <td>{r.status === 'pending' ? (<div className="row"><Btn kind="danger" busy={action.busy} onClick={() => void act(r.id, 'execute')}>Удалить данные</Btn><Btn kind="secondary" busy={action.busy} onClick={() => void act(r.id, 'reject')}>Отказать</Btn></div>) : STATUS_RU[r.status]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export function AdminsPage() {
  const qc = useQueryClient();
  const { me } = useAdmin();
  const action = useAction();
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ email: '', displayName: '', role: 'moderator', password: '' });
  const { data, isLoading, error } = useQuery({
    queryKey: ['admins'],
    queryFn: () => api<{ items: { id: string; email: string; displayName: string; role: string; isActive: boolean; lastLoginAt: string | null; twoFactorEnabled: boolean }[] }>('/admin/admins'),
  });
  const refresh = () => void qc.invalidateQueries({ queryKey: ['admins'] });
  return (
    <div>
      <h1>Администраторы</h1>
      <div className="toolbar"><Btn onClick={() => setCreating(true)}>Добавить</Btn></div>
      {action.error ? <ErrorBox error={action.error} /> : null}
      {isLoading ? <Loading /> : error ? <ErrorBox error={error} /> : (
        <table>
          <thead><tr><th>Имя</th><th>Email</th><th>Роль</th><th>Активен</th><th>2FA</th><th>Последний вход</th></tr></thead>
          <tbody>
            {data?.items.map((a) => (
              <tr key={a.id}>
                <td>{a.displayName}</td>
                <td>{a.email}</td>
                <td>
                  <select value={a.role} disabled={a.id === me.id} onChange={(e) => void action.run(async () => { await api(`/admin/admins/${a.id}`, { method: 'PATCH', json: { role: e.target.value } }); refresh(); })}>
                    {Object.entries(ROLE_RU).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </td>
                <td><input type="checkbox" checked={a.isActive} disabled={a.id === me.id} onChange={(e) => void action.run(async () => { await api(`/admin/admins/${a.id}`, { method: 'PATCH', json: { isActive: e.target.checked } }); refresh(); })} /></td>
                <td>
                  {a.twoFactorEnabled ? 'включена' : <span className="muted">нет</span>}
                  {a.twoFactorEnabled && a.id !== me.id && (a.role !== 'owner' || me.role === 'owner') ? (
                    <div><button className="link" disabled={action.busy} onClick={() => void action.run(async () => {
                      if (!window.confirm(`Сбросить 2FA для ${a.email}? Используйте при потере телефона; сессии администратора будут завершены.`)) return;
                      await api(`/admin/auth/2fa/reset/${a.id}`, { method: 'POST' });
                      refresh();
                    })}>сбросить</button></div>
                  ) : null}
                </td>
                <td>{fmtDate(a.lastLoginAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {creating ? (
        <Modal title="Новый администратор" onClose={() => setCreating(false)}>
          <label className="field">Email<input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></label>
          <label className="field">Имя<input value={form.displayName} onChange={(e) => setForm({ ...form, displayName: e.target.value })} /></label>
          <label className="field">Роль<select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>{Object.entries(ROLE_RU).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></label>
          <label className="field">Временный пароль (≥ 12 символов; передайте лично)<input type="password" autoComplete="new-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></label>
          <Btn busy={action.busy} disabled={form.password.length < 12} onClick={() => void action.run(async () => { await api('/admin/admins', { method: 'POST', json: form }); setCreating(false); setForm({ email: '', displayName: '', role: 'moderator', password: '' }); refresh(); })}>Создать</Btn>
          {action.error ? <ErrorBox error={action.error} /> : null}
        </Modal>
      ) : null}
    </div>
  );
}

export function AuditPage() {
  const [page, setPage] = useState(1);
  const [action, setAction] = useState('');
  const { data, isLoading, error } = useQuery({
    queryKey: ['audit', page, action],
    queryFn: () => api<Paged<{ id: string; admin: string | null; action: string; entityType: string; entityId: string | null; details: unknown; ip: string | null; createdAt: string }>>(`/admin/audit${qs({ page, pageSize: 50, action })}`),
  });
  return (
    <div>
      <h1>Журнал действий</h1>
      <div className="toolbar"><input placeholder="Действие (например, listing.)" value={action} onChange={(e) => { setAction(e.target.value); setPage(1); }} /></div>
      {isLoading ? <Loading /> : error ? <ErrorBox error={error} /> : !data?.items.length ? <Empty /> : (
        <>
          <table>
            <thead><tr><th>Время</th><th>Администратор</th><th>Действие</th><th>Объект</th><th>Детали</th><th>IP</th></tr></thead>
            <tbody>
              {data.items.map((r) => (
                <tr key={r.id}>
                  <td>{fmtDate(r.createdAt)}</td><td>{r.admin ?? 'система'}</td><td><code>{r.action}</code></td>
                  <td>{r.entityType} {r.entityId ?? ''}</td><td className="small"><code>{r.details ? JSON.stringify(r.details).slice(0, 160) : ''}</code></td><td>{r.ip}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pager page={page} pageSize={50} total={data.total} onPage={setPage} />
        </>
      )}
    </div>
  );
}
